import { useCallback } from "react";

import { useLazyGetLeanListQuery } from "../../../redux/list/list.api";

import { AnalyticsTab } from "../config/tabs";
import { ANALYTICS_TAB_PARAM_KEY } from "../utils/analyticsTab";
import { CampaignClickTarget, selectOnlyCampaign } from "../utils/selectedList";

import { AnalyticsSelection } from "./useAnalyticsSelection";

/**
 * The click-through from a campaign row (Overview, Top Campaigns) into the Campaign Deep
 * Dive, scoped to that campaign.
 *
 * The list is read first so the selection carries its other campaigns too, switched off —
 * otherwise the Campaign filter knows only the clicked one and reads "All" (PREC-4215). A
 * failed read still opens the campaign; it only costs the filter its other options.
 *
 * The narrowing and the tab change go out as one write via `selectListsWithParams`: two
 * separate writes race the selection hook's URL sync and one of them is silently dropped.
 */
export function useOpenCampaignDeepDive(
	selectListsWithParams: AnalyticsSelection["selectListsWithParams"]
): (target: CampaignClickTarget) => Promise<void> {
	const [getLeanList] = useLazyGetLeanListQuery();

	return useCallback(
		async (target: CampaignClickTarget) => {
			const list = await getLeanList({ id: target.listId }, true)
				.unwrap()
				.catch(() => undefined);

			selectListsWithParams([selectOnlyCampaign(target, list)], {
				[ANALYTICS_TAB_PARAM_KEY]: AnalyticsTab.CAMPAIGN_DEEP_DIVE,
			});
		},
		[getLeanList, selectListsWithParams]
	);
}
